// The Sign-in library screen (SCR-154, SCR-591, REQ-240): how people sign in on the org's proxies,
// in sub-tabs (ADR-115), each a page of its own with its own save over the paths it owns:
// Status & methods · Identity providers · Cognito · AWS sign-in · Admin access (org default only).
// Keys live on the Keys screen. Status & methods holds, on the proxy, the proxy's live status (the
// shell's `status`), and the methods' note; staff see "Sign-in served by" there as a read-only
// chip (the flip is AllCode's). The customer's admin sees no served-by line from the editor: the
// proxy's status states the applied value, and the org default's could disagree with it (SIG-2
// ⚠2). Nothing here takes or shows a secret.
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { HelpButton, type HelpId } from "../../primitives/help";
import { Card, Chip } from "../../primitives/page";
import { NotFound } from "../../primitives/states";
import { firstVisible, SubTabs, type SubTabDef } from "../../primitives/subtabs";
import { ScopedSettings, type Editor, type SettingsScope, type SignInScreenProps } from "./editor";
import { ADMIN_ACCESS_SECTION, AWS_SIGN_IN_SECTION, COGNITO_SECTION, SIGN_IN_EDITOR_ANCHOR, cognitoNeedsRedeploy, type PlainSection } from "./model";
import { SsoProviders } from "./sso-providers";

const METHODS_HELP: HelpId = "settings.sign_in";
const SERVED_BY_HELP: HelpId = "settings.sign_in.served_by";
const PROVIDERS_HELP: HelpId = "settings.sign_in.sso_providers";
const PROVIDERS_PATH = "sign_in.sso_providers";

/** One Sign-in sub-tab: its row entry, the paths its save owns, and its cards. */
interface SignInSubTab extends SubTabDef<SettingsScope> {
  owns: readonly string[];
  cards(editor: Editor): ComponentChildren;
}

/** Who serves sign-in, for staff only: a read-only chip. */
function ServedBy({ editor }: { editor: Editor }) {
  if (editor.surface !== "staff") return null;
  return (
    <p class="card-note">
      Sign-in served by <Chip tone="blue" title="Changed by AllCode staff with the flip, never by these settings">{editor.loaded.servedBy || "unknown"}</Chip>{" "}
      <span class="field-hint">Read-only here.</span>
      <HelpButton topic={SERVED_BY_HELP} label="Served by" />
    </p>
  );
}

function MethodsCard(editor: Editor) {
  return (
    <Card title="Sign-in methods" titleId={SIGN_IN_EDITOR_ANCHOR} help={METHODS_HELP}>
      <p class="card-note">
        These reach every proxy of this org within about a minute. Nothing here is a secret: an identity provider's client secret is entered by the org's admin on the proxy.
      </p>
      <ServedBy editor={editor} />
      {cognitoNeedsRedeploy(editor.loaded.needsRedeploy) && <p class="card-note">Cognito changes need a redeploy of the proxy before they apply.</p>}
    </Card>
  );
}

function ProvidersCard(editor: Editor) {
  const mode = editor.modeOf(PROVIDERS_PATH);
  const locked = mode.kind === "readonly";
  return (
    <Card title="Identity providers" help={PROVIDERS_HELP}>
      <SsoProviders providers={editor.providers} inherited={editor.providersInherited} readOnly={editor.readOnly || locked} error={editor.errors[PROVIDERS_PATH]} onChange={editor.setProviders} />
      {locked && <p class="card-note">{mode.note}.</p>}
    </Card>
  );
}

function plainCard(section: PlainSection) {
  return (editor: Editor) => (
    <Card title={section.title} help={section.help}>
      <div class="settings-grid">{section.paths.map(editor.field)}</div>
    </Card>
  );
}

function AdminAccessCard(editor: Editor) {
  return (
    <Card title={ADMIN_ACCESS_SECTION.title} help={ADMIN_ACCESS_SECTION.help}>
      <p class="card-note">Who manages this org's proxies: the default admins by email, admin teams and matchers. Set on the org default for every installation.</p>
      <div class="settings-grid">{ADMIN_ACCESS_SECTION.paths.map(editor.field)}</div>
    </Card>
  );
}

const always = () => true;

/**
 * The Sign-in sub-tabs (NAV-042), in display order. Admin access is absent on an installation's
 * scope: it is an org-only section (the API refuses it on an override). Its URL on an
 * installation's scope says so and offers the org default back.
 */
export const SIGN_IN_SUBTABS: readonly SignInSubTab[] = [
  { id: "status", label: "Status & methods", visible: always, owns: [], cards: MethodsCard },
  { id: "providers", label: "Identity providers", visible: always, owns: [PROVIDERS_PATH], cards: ProvidersCard },
  { id: "cognito", label: "Cognito", visible: always, owns: COGNITO_SECTION.paths, cards: plainCard(COGNITO_SECTION) },
  { id: "aws", label: "AWS sign-in", visible: always, owns: AWS_SIGN_IN_SECTION.paths, cards: plainCard(AWS_SIGN_IN_SECTION) },
  { id: "admins", label: "Admin access", visible: (scope) => scope === "org", owns: ADMIN_ACCESS_SECTION.paths, cards: AdminAccessCard },
];

/** The paths the Sign-in screen edits, over all its sub-tabs. */
export const SIGN_IN_PATHS: readonly string[] = SIGN_IN_SUBTABS.flatMap((tab) => tab.owns);

export interface SignInSubtabProps extends SignInScreenProps {
  /** The route's sub-tab segment; undefined at the section's bare URL. */
  subtab: string | undefined;
  /** The shell's URL of one of the sub-tabs. */
  subtabHref(id: string): string;
  /** The proxy's live sign-in status, shown above the methods on Status & methods (the proxy only). */
  status?: ComponentChildren;
}

export function SignInScreen({ context, scope: initialScope, subtab, subtabHref, status }: SignInSubtabProps) {
  // The scope the AllCode admin's selector chose stays as the person moves between sub-tabs.
  const [scope, setScope] = useState<SettingsScope>(initialScope);
  const current = subtab ?? firstVisible(SIGN_IN_SUBTABS, scope);
  const tab = SIGN_IN_SUBTABS.find((t) => t.id === current && t.visible(scope));
  return (
    <>
      <SubTabs tabs={SIGN_IN_SUBTABS} scope={scope} current={current} href={subtabHref} label="Sign-in" />
      {tab?.id === "status" && status}
      {tab ? (
        // Keyed: each sub-tab loads, drafts and saves on its own.
        <ScopedSettings
          key={tab.id}
          context={context}
          scope={scope}
          onScopeChange={setScope}
          label="Sign-in settings"
          saveLabel="Save sign-in"
          loadingLabel="Loading sign-in settings…"
          owns={tab.owns}
        >
          {tab.cards}
        </ScopedSettings>
      ) : current === "admins" ? (
        <p class="card-note">
          Admin access is set on the org default only.{" "}
          <button type="button" class="button button-sm" onClick={() => setScope("org")}>
            Show the org default
          </button>
        </p>
      ) : (
        <NotFound />
      )}
    </>
  );
}
